<template>
  <v-card>
    <v-toolbar flat color="white" density="compact">
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          :disabled="!canEdit"
          v-model="record.attributes_orientation"
          :return-object="false"
          item-value="value"
          item-title="text"
          :items="orientation_options"
          hide-details
          single-line>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('attributes_orientation') }}</span>
        </template>
      </v-autocomplete>
      <v-spacer/>
      <div class="chip-container">
        <base-action-button v-if="canEdit" @action="createAttribute">
          <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("create") }}
        </base-action-button>
        <base-action-button v-if="canEdit" @action="$emit('save')">
          <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
          {{ $store.getters.translate("save") }}
        </base-action-button>
      </div>
    </v-toolbar>
    <v-data-table
        id="base_table_attributes"
        :headers="headers"
        :items="record.attributes"
        class="elevation-1"
        :show-select="false"
        :items-per-page="100">
      <template #body="{ items }">
        <draggable :disabled="!canEdit" :list="items" tag="tbody" @end="endDrag(items)" item-key="id">
          <template #item="{ element:attribute }">
            <tr class="sm-normal text-primary">
              <td><PhArrowsVertical size="20" weight="bold" style="cursor: pointer; vertical-align: middle;"/></td>
              <td><a class="sm-bold" @click="editAttribute(attribute)">{{ attribute }}</a></td>
              <!--ACTION BUTTONS-->
              <td style="text-align: center; width: 80px;" v-if="canEdit">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn elevation="0" icon v-bind="props">
                      <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item v-if="canEdit" @click="editAttribute(attribute)">
                      <template #prepend><PhPencilSimpleLine size="24"/></template>
                      <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="canEdit" @click="deleteAttribute(attribute)">
                      <template #prepend><PhTrash size="24"/></template>
                      <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </draggable>
      </template>
      <template #bottom/>
    </v-data-table>
  </v-card>
  <create-attribute ref="create_attribute" @created="attributeCreated"/>
  <edit-attribute ref="edit_attribute" @edited="attributeEdited"/>
</template>

<script>
import { useAbility } from '@casl/vue';
import helpFunctions from "../../plugins/helpFunctions.js";
import draggable from "vuedraggable";
import BaseActionButton from "./BaseActionButton";
import CreateAttribute from "../../views/forms/attributes/Create";
import EditAttribute from "../../views/forms/attributes/Edit";
import { PhArrowsVertical, PhDotsThreeVertical, PhFloppyDisk, PhPencilSimpleLine, PhPlus, PhTrash } from "@phosphor-icons/vue";

export default {
  props: ["page", "record"],
  components: {
    draggable,
    BaseActionButton,
    CreateAttribute,
    EditAttribute,
    PhArrowsVertical, PhTrash, PhPencilSimpleLine, PhDotsThreeVertical, PhFloppyDisk, PhPlus
  },
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      headers: helpFunctions.headers.attributes,
      orientation_options: [{ value: "P", text: this.$store.getters.translate("P") }, { value: "L", text: this.$store.getters.translate("L") }],
      edited_attribute_name: ''
    };
  },
  beforeMount() {
    document.querySelector('#base_table_attributes > .v-table__wrapper > table > tbody')?.remove();
  },
  mounted() {
    const sortableTbody = document.querySelector('#base_table_attributes > .v-table__wrapper > table > tbody > tbody');
    // <draggable> ruin view in <tbody> (create a <div> tag inside <tbody>)
    //FIX: https://github.com/SortableJS/Vue.Draggable/issues/1212#issuecomment-1918880591
    if (sortableTbody) {
      const tbody = document.querySelector('#base_table_attributes > .v-table__wrapper > table > tbody')
      tbody.parentNode.append(sortableTbody);
      tbody.remove()
    }
  },
  methods: {
    createAttribute() {
      this.$refs.create_attribute.load();
    },
    attributeCreated(new_attribute_name) {
      this.record.attributes.push(new_attribute_name);
      this.$emit('save');
    },
    editAttribute(attribute) {
      this.edited_attribute_name = attribute;
      this.$refs.edit_attribute.load(attribute);
    },
    attributeEdited(new_attribute_name) {
      if(new_attribute_name !== this.edited_attribute_name) {
        for (let i = 0; i < this.record.attributes.length; i++) {
          if(this.record.attributes[i] === this.edited_attribute_name) {
            this.record.attributes[i] = new_attribute_name;
            this.edited_attribute_name = '';
            break;
          }
        }
        this.$emit('save');
      }
    },
    deleteAttribute(removed_attribute) {
      this.record.attributes = this.record.attributes.filter((attribute) => attribute !== removed_attribute);
      this.$emit('save');
    },
    endDrag(items) {
      this.record.attributes = items;
    },
  },
  computed: {
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
  },
};
</script>

<style scoped>
.v-checkbox {
  display: flex;
}
.chip-container {
  display: flex;
  gap: 8px;
  margin-right: 16px;
}
</style>